import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { WizardStepper } from "./components/WizardStepper";
import { Step1Setup } from "./steps/Step1Setup";
import { Step2Upload } from "./steps/Step2Upload";
import { Step3Mapping } from "./steps/Step3Mapping";
import { Step4Review } from "./steps/Step4Review";
import { Step5Progress } from "./steps/Step5Progress";
import {
  STATUS_LABELS,
  isKnownImportStatus,
  statusPillClass,
  wizardStepFor,
} from "../../lib/importStatus";
import { Button } from "../../components/ui/button";
import { AlertTriangle, ChevronLeft } from "lucide-react";

type ImportJob = {
  id: string;
  status: string;
  entity_type: string;
  version: number;
  scope_type?: string;
  file?: { filename?: string; byte_size?: number };
  counts?: Record<string, number>;
  cancel_requested_at?: string | null;
};

/**
 * ImportWizard is the shell that:
 *   - renders Step 1 (Setup) when no jobId is in the URL,
 *   - fetches the job by id and derives the current step from `job.status`,
 *   - renders a safe recovery screen if the status is unknown,
 *   - re-fetches after any mutating action via `refetch()`.
 *
 * Milestone D1 wires Steps 1 & 2 only. Steps 3-5 render a placeholder card
 * pointing at their D2/D3/D4 implementation slices. This keeps the shell
 * production-shippable at every slice boundary.
 */
export default function ImportWizard() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<ImportJob | null>(null);
  const [loading, setLoading] = useState<boolean>(!!jobId);
  const [notFound, setNotFound] = useState(false);

  const refetch = useCallback(async () => {
    if (!jobId) return;
    try {
      const { data } = await api.get<ImportJob>(`/import/jobs/${jobId}`);
      setJob(data);
      setNotFound(false);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotFound(true);
        setJob(null);
      } else {
        toastError(e, "Could not load the import job.");
      }
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    refetch();
  }, [jobId, refetch]);

  const step = jobId ? (job ? wizardStepFor(job.status) : null) : 1;
  const unknownStatus = !!jobId && !!job && !isKnownImportStatus(job.status);

  return (
    <>
      <PageHeader
        title={jobId ? "Import job" : "New import"}
        subtitle={
          job
            ? `Job ${job.id.slice(0, 8)}… · ${job.entity_type}`
            : "Contact CSV import wizard"
        }
        actions={
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => navigate("/imports")}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to imports
          </Button>
        }
      />
      <div className="max-w-5xl mx-auto p-6 md:p-8 space-y-6">
        <WizardStepper status={job?.status} />

        {loading && (
          <div className="text-sm text-slate-500" data-testid="wizard-loading">
            Loading import job…
          </div>
        )}

        {notFound && (
          <div
            className="rounded-md border border-rose-200 bg-rose-50 p-4"
            data-testid="wizard-not-found"
          >
            <div className="text-sm font-medium text-rose-900">Import job not found</div>
            <div className="text-sm text-rose-800/80 mt-0.5">
              This job doesn&apos;t exist or your account cannot access it.
            </div>
          </div>
        )}

        {unknownStatus && (
          <SafeRecoveryScreen status={job!.status} onBack={() => navigate("/imports")} />
        )}

        {!loading && !notFound && !unknownStatus && (
          <div
            className="rounded-lg border border-slate-200 bg-white p-5 md:p-6"
            data-testid={`wizard-step-${step ?? "recover"}`}
          >
            {step === 1 && <Step1Setup />}

            {step === 2 && job && (
              <Step2Upload
                jobId={job.id}
                file={job.file || null}
                onJobUpdated={refetch}
              />
            )}

            {step === 3 && job && (
              <Step3Mapping job={job} onJobUpdated={refetch} />
            )}

            {step === 4 && job && (
              <Step4Review job={job} onJobUpdated={refetch} />
            )}

            {step === 5 && job && (
              <Step5Progress job={job} onJobUpdated={refetch} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

function StepPlaceholder(_: { step: number; status: string }) {
  // No pending steps after D4 — Step 5 is fully wired.
  return null;
}

function SafeRecoveryScreen({ status, onBack }: { status: string; onBack: () => void }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-amber-200 bg-amber-50 p-4"
      data-testid="wizard-safe-recovery"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-amber-900">
            This import is in an unknown state
          </div>
          <div className="text-sm text-amber-800/80 mt-0.5">
            The wizard doesn&apos;t recognize <span className="font-mono">{status}</span>. This can
            happen right after an app upgrade. No data has been lost; go back to the imports
            list and open the job again once the page is refreshed.
          </div>
          <div className="mt-3">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={onBack}
              data-testid="safe-recovery-back-btn"
            >
              Back to imports
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
